import type { Theme } from '../types/theme.types';

export const lightTheme: Theme = {
	id: 'light',
	name: 'light',
	version: '1.0.0',
	description: 'Default light theme for SupplyMover',

	colors: {
		// CSS color values only - no Tailwind classes
		values: {
			// Core semantic colors
			primary: '#2563EB',
			primaryHover: '#1D4ED8',
			primaryLight: '#DBEAFE',
			primaryDark: '#1E40AF',

			secondary: '#6B7280',
			secondaryHover: '#4B5563',
			secondaryLight: '#F3F4F6',

			success: '#10B981',
			successHover: '#059669',
			successLight: '#D1FAE5',

			warning: '#F59E0B',
			warningHover: '#D97706',
			warningLight: '#FEF3C7',

			danger: '#EF4444',
			dangerHover: '#DC2626',
			dangerLight: '#FEE2E2',

			info: '#3B82F6',
			infoHover: '#2563EB',
			infoLight: '#DBEAFE',

			// UI elements
			background: '#FFFFFF',
			surface: '#F9FAFB',
			surfaceElevated: '#FFFFFF',
			container: '#FFFFFF',

			textPrimary: '#111827',
			textSecondary: '#6B7280',
			textTertiary: '#9CA3AF',
			textMuted: '#9CA3AF',
			textInverse: '#FFFFFF',

			borderLight: '#E5E7EB',
			borderMedium: '#D1D5DB',
			borderHeavy: '#9CA3AF',
			borderFocus: '#3B82F6',

			// Navigation specific
			navBackground: '#111827',
			navText: '#E5E7EB',
			navTextActive: '#FFFFFF',
			navHover: '#1F2937',
			navActive: '#1F2937',
			navIconDefault: '#9CA3AF',
			navIconActive: '#FFFFFF',
			navScrollbarTrack: '#111827',
			navScrollbarThumb: '#374151',
			navScrollbarThumbHover: '#4B5563',

			// General scrollbar colors (for non-nav scrollbars)
			scrollbarTrack: '#F3F4F6',
			scrollbarThumb: '#D1D5DB',
			scrollbarThumbHover: '#9CA3AF',

			// Chart colors
			chart1: '#3B82F6',
			chart2: '#10B981',
			chart3: '#F59E0B',
			chart4: '#8B5CF6',
			chart5: '#EC4899',
			chart6: '#6366F1',

			// Chart trend colors (for sparklines and trend indicators)
			chartTrendUp: '#22C55E',
			chartTrendDown: '#EF4444',
			chartTrendNeutral: '#9CA3AF',

			// Icon colors
			iconPrimary: '#374151',
			iconSecondary: '#9CA3AF',
			iconInverse: '#FFFFFF',
			iconDanger: '#EF4444',
			iconSuccess: '#10B981',
			iconWarning: '#F59E0B',
			iconInfo: '#3B82F6',

			// Navigation icon colors (numbered for reuse)
			navIcon1: '#3B82F6', // Blue
			navIcon2: '#10B981', // Green
			navIcon3: '#F59E0B', // Amber
			navIcon4: '#8B5CF6', // Purple
			navIcon5: '#EF4444', // Red
			navIcon6: '#EC4899', // Pink
			navIcon7: '#6366F1', // Indigo
			navIcon8: '#14B8A6', // Teal
			navIcon9: '#F97316', // Orange
			navIcon10: '#0EA5E9', // Sky
			navIcon11: '#A855F7', // Violet
			navIcon12: '#06B6D4', // Cyan
			navIcon13: '#84CC16', // Lime
			navIcon14: '#EAB308', // Yellow
			navIcon15: '#22C55E', // Emerald
			navIcon16: '#DC2626', // Rose

			// Chip/pill colors (numbered for reuse)
			chipColor1: '#93C5FD', // Blue 300
			chipColor2: '#86EFAC', // Green 300
			chipColor3: '#FDE68A', // Amber 300
			chipColor4: '#C4B5FD', // Purple 300
			chipColor5: '#FCA5A5', // Red 300
			chipColor6: '#F9A8D4', // Pink 300
			chipColor7: '#A5B4FC', // Indigo 300
			chipColor8: '#5EEAD4', // Teal 300
			chipColor9: '#FDBA74', // Orange 300
			chipColor10: '#7DD3FC', // Sky 300
			chipColor11: '#C4B5FD', // Violet 300
			chipColor12: '#67E8F9', // Cyan 300
			chipColor13: '#BEF264', // Lime 300
			chipColor14: '#FDE047', // Yellow 300
			chipColor15: '#6EE7B7', // Emerald 300
			chipColor16: '#FDA4AF', // Rose 300

			// Interactive backgrounds (with opacity)
			primaryBg: 'rgba(59, 130, 246, 0.1)',
			successBg: 'rgba(16, 185, 129, 0.1)',
			warningBg: 'rgba(245, 158, 11, 0.1)',
			dangerBg: 'rgba(239, 68, 68, 0.1)',
			infoBg: 'rgba(59, 130, 246, 0.1)',

			// Toast colors (for borders)
			toastSuccess: '#22c55e',
			toastWarning: '#f59e0b',
			toastError: '#ef4444',
			toastInfo: '#3b82f6',

			// Overlay
			overlay: 'rgba(0, 0, 0, 0.5)',
			shadow: 'rgba(0, 0, 0, 0.1)',

			// Focus ring colors
			focusRingPrimary: '#3B82F6',
			focusRingDanger: '#EF4444',
			focusRingSuccess: '#10B981',
			focusRingWarning: '#F59E0B',
			focusRingDefault: '#6B7280',

			// Aura ring colors for AI agent interface
			auraUserSpeaking1: '#8B5CF6', // Purple gradient start
			auraUserSpeaking2: '#A78BFA', // Purple gradient middle
			auraUserSpeaking3: '#C4B5FD', // Purple gradient end
			auraAgentSpeaking1: '#D97706', // Rich amber start
			auraAgentSpeaking2: '#F59E0B', // Golden amber middle
			auraAgentSpeaking3: '#FCD34D', // Light amber end
			auraIdle1: '#6B7280', // Gray gradient start
			auraIdle2: '#9CA3AF', // Gray gradient middle
			auraIdle3: '#D1D5DB', // Gray gradient end
			auraMuted1: '#EF4444', // Red gradient start
			auraMuted2: '#F87171', // Red gradient middle
			auraMuted3: '#FCA5A5', // Red gradient end

			// Heat map gradient colors (light to dark green)
			heatMapGreen1: '#d4f4dd', // Lightest green
			heatMapGreen2: '#b8ecc8',
			heatMapGreen3: '#9ce4b3',
			heatMapGreen4: '#80dc9e',
			heatMapGreen5: '#64d489',
			heatMapGreen6: '#48cc74',
			heatMapGreen7: '#2cc45f',
			heatMapGreen8: '#22a34e',
			heatMapGreen9: '#18823d',
			heatMapGreen10: '#0d7938', // Darkest green

			// Email template colors
			emailBackground: '#F3F4F6',
			emailContainerBackground: '#FFFFFF',
			emailTextPrimary: '#111827',
			emailTextSecondary: '#6B7280',
			emailLinkColor: '#3B82F6',
			emailButtonBackground: '#3B82F6',
			emailButtonText: '#FFFFFF',
			emailBorderColor: '#E5E7EB',
			emailHeaderBackground: '#1F2937',
			emailHeaderText: '#FFFFFF',
			emailFooterBackground: '#F9FAFB',
			emailFooterText: '#6B7280',

			// Automation flow node icon colors (existing)
			automationStart: '#3B82F6', // Blue for start nodes
			automationTemplate: '#3B82F6', // Blue for template nodes
			automationCondition: '#FCD34D', // Light yellow/gold for condition nodes
			automationConditionHeader: '#FEF3C7', // Very light yellow for condition header background
			automationDelay: '#6B7280', // Gray for delay nodes
			automationAction: '#10B981', // Green for action nodes
			automationSplit: '#3B82F6', // Blue for split nodes
			automationAgent: '#3B82F6', // Blue for agent nodes

			// React Flow automation designer colors
			automationNodeBg: '#FFFFFF',
			automationNodeBorder: '#D1D5DB',
			automationNodeText: '#111827',
			automationNodeHover: '#F3F4F6',
			automationNodeSelected: '#3B82F6',

			automationConnectionDefault: '#9CA3AF',
			automationConnectionHover: '#6B7280',
			automationConnectionTrue: '#10B981',
			automationConnectionFalse: '#EF4444',
			automationConnectionSplitA: '#3B82F6',
			automationConnectionSplitB: '#8B5CF6',

			automationHandleDefault: '#D1D5DB',
			automationHandleActive: '#3B82F6',
			automationHandleHover: '#6B7280',

			automationLabelBg: '#FFFFFF',
			automationLabelText: '#374151',
			automationLabelBorder: '#E5E7EB',

			// Automation node border colors
			automationStartBorder: '#2563EB', // Strong blue for start nodes
			automationTemplateBorder: '#F59E0B', // Orange for template nodes
			automationConditionBorder: '#F59E0B', // Orange for condition nodes
			automationConditionIncompleteBorder: '#DC2626', // Red-orange for incomplete conditions
			automationDelayBorder: '#F59E0B', // Orange for delay nodes
			automationActionBorder: '#F59E0B', // Orange for action nodes
			automationSplitBorder: '#F59E0B', // Orange for split nodes
			automationAgentBorder: '#F59E0B', // Orange for agent nodes
			automationDefaultBorder: '#F59E0B', // Orange default
			automationIncompleteBorder: '#EF4444', // Red for any incomplete node

			// Automation selection aura colors (match border colors with transparency)
			automationStartAura: '#2563EB',
			automationTemplateAura: '#F59E0B',
			automationConditionAura: '#F59E0B',
			automationConditionIncompleteAura: '#DC2626',
			automationDelayAura: '#F59E0B',
			automationActionAura: '#F59E0B',
			automationSplitAura: '#F59E0B',
			automationAgentAura: '#F59E0B',
			automationDefaultAura: '#F59E0B',
			automationIncompleteAura: '#EF4444',

			// Agent chat UI colors for proper contrast
			agentMessageBg: '#F3F4F6', // Light gray background for agent messages
			agentMessageText: '#111827', // Dark text for agent messages
			userMessageBg: '#3B82F6', // Blue background for user messages
			userMessageText: '#FFFFFF', // White text for user messages
			dangerButtonText: '#FFFFFF', // Always white text on danger buttons
			muted: '#6B7280', // Gray for muted/disabled states
		},

		// Tailwind classes are provided by ThemeService, not theme files
		classes: {} as any, // ThemeService will populate this

		// Logo paths
		logo: {
			primary: '/assets/supply-mover-logo-small.png',
			inverse: '/assets/supply-mover-logo-small-white.png',
		},
	},

	typography: {
		fontFamily: {
			sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
			mono: 'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
		},

		scale: {
			xs: { size: '0.75rem', weight: 400, lineHeight: 1.5 },
			sm: { size: '0.875rem', weight: 400, lineHeight: 1.5 },
			base: { size: '1rem', weight: 400, lineHeight: 1.5 },
			lg: { size: '1.125rem', weight: 400, lineHeight: 1.5 },
			xl: { size: '1.25rem', weight: 400, lineHeight: 1.5 },
			'2xl': { size: '1.5rem', weight: 600, lineHeight: 1.3 },
			'3xl': { size: '1.875rem', weight: 600, lineHeight: 1.3 },
			'4xl': { size: '2.25rem', weight: 700, lineHeight: 1.2 },
			'5xl': { size: '3rem', weight: 700, lineHeight: 1.2 },
		},

		heading: {
			h1: { size: '3rem', weight: 700, lineHeight: 1.2 },
			h2: { size: '2.25rem', weight: 600, lineHeight: 1.3 },
			h3: { size: '1.875rem', weight: 600, lineHeight: 1.3 },
			h4: { size: '1.5rem', weight: 600, lineHeight: 1.4 },
			h5: { size: '1.25rem', weight: 600, lineHeight: 1.5 },
			h6: { size: '1.125rem', weight: 600, lineHeight: 1.5 },
		},
	},

	spacing: {
		xs: '0.25rem',
		sm: '0.5rem',
		md: '1rem',
		lg: '1.5rem',
		xl: '2rem',
		'2xl': '3rem',
		'3xl': '4rem',
	},

	borderRadius: {
		none: '0',
		sm: '0.125rem',
		base: '0.25rem',
		md: '0.375rem',
		lg: '0.5rem',
		xl: '0.75rem',
		'2xl': '1rem',
		'3xl': '1.5rem',
		full: '9999px',
	},

	shadows: {
		xs: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
		sm: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
		base: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
		md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
		lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
		xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
		'2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
		inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
		none: 'none',
	},

	// CSS variables mapping - this is what gets applied to the DOM
	cssVars: {
		// Core colors
		'--color-primary': '#3B82F6',
		'--color-primary-hover': '#2563EB',
		'--color-primary-light': '#DBEAFE',
		'--color-primary-dark': '#1E40AF',

		'--color-secondary': '#6B7280',
		'--color-secondary-hover': '#4B5563',
		'--color-secondary-light': '#F3F4F6',

		'--color-success': '#10B981',
		'--color-success-hover': '#059669',
		'--color-success-light': '#D1FAE5',

		'--color-warning': '#F59E0B',
		'--color-warning-hover': '#D97706',
		'--color-warning-light': '#FEF3C7',

		'--color-danger': '#EF4444',
		'--color-danger-hover': '#DC2626',
		'--color-danger-light': '#FEE2E2',

		'--color-info': '#3B82F6',
		'--color-info-hover': '#2563EB',
		'--color-info-light': '#DBEAFE',

		// UI colors
		'--color-background': '#FFFFFF',
		'--color-surface': '#F9FAFB',
		'--color-surface-elevated': '#FFFFFF',
		'--color-container': '#FFFFFF',

		'--color-text-primary': '#111827',
		'--color-text-secondary': '#6B7280',
		'--color-text-muted': '#9CA3AF',
		'--color-text-inverse': '#FFFFFF',

		'--color-border-light': '#E5E7EB',
		'--color-border-medium': '#D1D5DB',
		'--color-border-heavy': '#9CA3AF',
		'--color-border-focus': '#3B82F6',

		// Navigation
		'--color-nav-bg': '#111827',
		'--color-nav-text': '#E5E7EB',
		'--color-nav-text-active': '#FFFFFF',
		'--color-nav-hover': '#1F2937',
		'--color-nav-active': '#1F2937',
		'--color-nav-icon-default': '#9CA3AF',
		'--color-nav-icon-active': '#FFFFFF',
		'--color-nav-scrollbar-track': '#111827',
		'--color-nav-scrollbar-thumb': '#374151',
		'--color-nav-scrollbar-thumb-hover': '#4B5563',

		// General scrollbar colors
		'--color-scrollbar-track': '#F3F4F6',
		'--color-scrollbar-thumb': '#D1D5DB',
		'--color-scrollbar-thumb-hover': '#9CA3AF',

		// Interactive backgrounds
		'--color-interactive-primary': 'rgba(59, 130, 246, 0.1)',
		'--color-interactive-success': 'rgba(16, 185, 129, 0.1)',
		'--color-interactive-warning': 'rgba(245, 158, 11, 0.1)',
		'--color-interactive-danger': 'rgba(239, 68, 68, 0.1)',
		'--color-interactive-info': 'rgba(59, 130, 246, 0.1)',

		// Chart colors
		'--color-chart-1': '#3B82F6',
		'--color-chart-2': '#10B981',
		'--color-chart-3': '#F59E0B',
		'--color-chart-4': '#8B5CF6',
		'--color-chart-5': '#EC4899',
		'--color-chart-6': '#6366F1',

		// Chart trend colors
		'--color-chart-trend-up': '#22C55E',
		'--color-chart-trend-down': '#EF4444',
		'--color-chart-trend-neutral': '#9CA3AF',

		// Icon colors
		'--color-icon-primary': '#374151',
		'--color-icon-secondary': '#9CA3AF',
		'--color-icon-inverse': '#FFFFFF',
		'--color-icon-danger': '#EF4444',
		'--color-icon-success': '#10B981',
		'--color-icon-warning': '#F59E0B',
		'--color-icon-info': '#3B82F6',

		// Toast colors
		'--color-toast-success': '#22c55e',
		'--color-toast-warning': '#f59e0b',
		'--color-toast-error': '#ef4444',
		'--color-toast-info': '#3b82f6',

		// Overlay
		'--color-overlay': 'rgba(0, 0, 0, 0.5)',
		'--color-shadow': 'rgba(0, 0, 0, 0.1)',

		// Focus ring colors
		'--color-focus-ring-primary': '#3B82F6',
		'--color-focus-ring-danger': '#EF4444',
		'--color-focus-ring-success': '#10B981',
		'--color-focus-ring-warning': '#F59E0B',
		'--color-focus-ring-default': '#6B7280',

		// Heat map gradient colors
		'--color-heat-map-green-1': '#d4f4dd',
		'--color-heat-map-green-2': '#b8ecc8',
		'--color-heat-map-green-3': '#9ce4b3',
		'--color-heat-map-green-4': '#80dc9e',
		'--color-heat-map-green-5': '#64d489',
		'--color-heat-map-green-6': '#48cc74',
		'--color-heat-map-green-7': '#2cc45f',
		'--color-heat-map-green-8': '#22a34e',
		'--color-heat-map-green-9': '#18823d',
		'--color-heat-map-green-10': '#0d7938',

		// Email template colors
		'--color-email-background': '#F3F4F6',
		'--color-email-container-background': '#FFFFFF',
		'--color-email-text-primary': '#111827',
		'--color-email-text-secondary': '#6B7280',
		'--color-email-link': '#3B82F6',
		'--color-email-button-background': '#3B82F6',
		'--color-email-button-text': '#FFFFFF',
		'--color-email-border': '#E5E7EB',
		'--color-email-header-background': '#1F2937',
		'--color-email-header-text': '#FFFFFF',
		'--color-email-footer-background': '#F9FAFB',
		'--color-email-footer-text': '#6B7280',

		// Agent chat UI colors
		'--color-agent-message-bg': '#F3F4F6',
		'--color-agent-message-text': '#111827',
		'--color-user-message-bg': '#3B82F6',
		'--color-user-message-text': '#FFFFFF',
		'--color-danger-button-text': '#FFFFFF',

		// Typography
		'--font-family-sans':
			'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
		'--font-family-mono':
			'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',

		'--font-size-xs': '0.75rem',
		'--font-size-sm': '0.875rem',
		'--font-size-base': '1rem',
		'--font-size-lg': '1.125rem',
		'--font-size-xl': '1.25rem',
		'--font-size-2xl': '1.5rem',
		'--font-size-3xl': '1.875rem',
		'--font-size-4xl': '2.25rem',
		'--font-size-5xl': '3rem',

		'--font-weight-light': '300',
		'--font-weight-normal': '400',
		'--font-weight-medium': '500',
		'--font-weight-semibold': '600',
		'--font-weight-bold': '700',

		'--line-height-tight': '1.25',
		'--line-height-normal': '1.5',
		'--line-height-relaxed': '1.625',

		// Spacing
		'--spacing-xs': '0.25rem',
		'--spacing-sm': '0.5rem',
		'--spacing-md': '1rem',
		'--spacing-lg': '1.5rem',
		'--spacing-xl': '2rem',
		'--spacing-2xl': '3rem',
		'--spacing-3xl': '4rem',

		// Border radius
		'--radius-none': '0',
		'--radius-sm': '0.125rem',
		'--radius-base': '0.25rem',
		'--radius-md': '0.375rem',
		'--radius-lg': '0.5rem',
		'--radius-xl': '0.75rem',
		'--radius-2xl': '1rem',
		'--radius-3xl': '1.5rem',
		'--radius-full': '9999px',

		// Shadows
		'--shadow-xs': '0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'--shadow-sm': '0 1px 3px 0 rgb(0 0 0 / 0.1)',
		'--shadow-base': '0 1px 3px 0 rgb(0 0 0 / 0.1)',
		'--shadow-md': '0 4px 6px -1px rgb(0 0 0 / 0.1)',
		'--shadow-lg': '0 10px 15px -3px rgb(0 0 0 / 0.1)',
		'--shadow-xl': '0 20px 25px -5px rgb(0 0 0 / 0.1)',
		'--shadow-2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
		'--shadow-inner': 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
		'--shadow-none': 'none',
	},

	// Metadata
	createdAt: '2024-01-01T00:00:00Z',
	updatedAt: '2024-01-01T00:00:00Z',
	author: 'SupplyMover Team',
};
